export type OrgProjectScope = {
  organizationId: string;
  projectId?: string;
};

/**
 * Builds the dashboard "home" path for a given org/project scope, degrading up
 * the hierarchy: a full org+project scope lands on the project dashboard, an
 * org-only scope on the org index (which redirects to that org's default
 * project), and an empty scope on the app root (which resolves the account's
 * default org+project).
 */
export function buildHomePath(scope: {
  organizationId?: string | null;
  projectId?: string | null;
}): string {
  const { organizationId, projectId } = scope;

  if (organizationId && projectId) {
    return `/orgs/${organizationId}/projects/${projectId}/home`;
  }
  if (organizationId) {
    return `/orgs/${organizationId}`;
  }
  return "/";
}

const ORG_PROJECT_PATHNAME = /^\/orgs\/([^/]+)(?:\/projects\/([^/]+))?/;

/**
 * Extracts the org/project scope from a pathname, or `null` when the path is not
 * scoped to an org (e.g. `/login`, `/403`). Only the org and project id segments
 * are captured — any current sub-page (`/mcp-gateways`, `/auth/secrets/…`) is
 * discarded so callers always land on the clean project home.
 */
export function parseOrgProjectScope(pathname: string): OrgProjectScope | null {
  const match = ORG_PROJECT_PATHNAME.exec(pathname);
  if (!match) {
    return null;
  }

  const [, organizationId, projectId] = match;
  return projectId ? { organizationId, projectId } : { organizationId };
}

/**
 * Derives the home path from a pathname so an error screen can return the user to
 * the org/project they were in rather than the account default.
 */
export function deriveHomePath(pathname: string): string {
  return buildHomePath(parseOrgProjectScope(pathname) ?? {});
}
